import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthAPI } from "../api/authAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { triggerGa } from "../utils/gaUtils";
import useClarity from "../hooks/useClarity";
import { setUserPropertiesGa } from "../utils/gaUtils";
import { useSessionState } from "../hooks/useSessionState";
import {
  APPROVED_THROUGH_DIY_FLOW_USER_STATUS,
  APPROVED_USER_STATUS,
  DEFAULT_ADMIN_SELECTED_BRAND,
  DEFAULT_ADMIN_SELECTED_ONBOARDING_BRAND,
  EMAIL_USER_TYPE,
  LOCALHOST_DOMAINS,
  ONBOARDING_USER_STATUS,
} from "../utils/constants";
import { clearStorageExcept } from "../utils/helpers";

// Exported for component tests that need a minimal provider (e.g. Discover ListView/CardView).
export const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserIdClarity } = useClarity();

  const [emailUserAuthToken, setEmailUserAuthToken] = useState(
    () =>
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("authToken") ||
      null,
  );
  const [adminAuthToken, setAdminAuthToken] = useState(
    () =>
      localStorage.getItem("Token") || sessionStorage.getItem("Token") || null,
  );

  const [selectedBrand, setSelectedBrand] = useSessionState(
    `admin-brand`,
    DEFAULT_ADMIN_SELECTED_BRAND,
  );
  const [selectedOnboardingBrand, setSelectedOnboardingBrand] = useSessionState(
    `admin-onboarding-brand`,
    DEFAULT_ADMIN_SELECTED_ONBOARDING_BRAND,
  );
  const [selectedEmailUserBrand, setSelectedEmailUserBrand] = useSessionState(
    `email-user-brand`,
    null,
  );

  const activeBrandContext = adminAuthToken
    ? selectedOnboardingBrand
      ? { onboarding_brand_id: selectedOnboardingBrand.id }
      : { brand_id: selectedBrand?.id }
    : { brand_id: selectedEmailUserBrand?.id };

  const {
    data: user,
    isPending: isUserDataLoading,
    isError: isUserDataError,
    error: userError,
  } = useQuery({
    queryKey: ["currentUser", activeBrandContext],
    queryFn: async () => {
      if (!emailUserAuthToken && !adminAuthToken) throw new Error("No token");
      return AuthAPI.getCurrentUser({
        brand_id: adminAuthToken
          ? selectedBrand?.id
          : selectedEmailUserBrand?.id,
        onboarding_brand_id: adminAuthToken
          ? selectedOnboardingBrand?.id
          : undefined,
      });
    },
    enabled: !!emailUserAuthToken || !!adminAuthToken,
    retry: (failureCount, error) => {
      const isAuthError = [401, 403].includes(error?.response?.status);
      return !isAuthError && failureCount < 3;
    },
    retryDelay: 1000,
    refetchInterval: 1000 * 60 * 10,
    refetchOnWindowFocus: "always",
  });

  const { data: brandList } = useQuery({
    queryKey: ["brandList"],
    queryFn: async () => {
      if (!adminAuthToken) throw new Error("No token");
      return AuthAPI.getBrands();
    },
    enabled: !!adminAuthToken,
    retry: (failureCount, error) => {
      const isAuthError = [401, 403].includes(error?.response?.status);
      return !isAuthError && failureCount < 3;
    },
    refetchOnWindowFocus: "always",
  });

  const { data: onboardingBrandList } = useQuery({
    queryKey: ["onboardingBrandList"],
    queryFn: async () => {
      if (!adminAuthToken) throw new Error("No token");
      return AuthAPI.getOnboardingBrands();
    },
    enabled: !!adminAuthToken,
    retry: (failureCount, error) => {
      const isAuthError = [401, 403].includes(error?.response?.status);
      return !isAuthError && failureCount < 3;
    },
    refetchOnWindowFocus: "always",
  });

  useEffect(() => {
    if (!adminAuthToken || !selectedOnboardingBrand?.finalized_brand_id) return;

    const finalizedBrand = (brandList?.list || []).find(
      (brand) => brand.id === selectedOnboardingBrand.finalized_brand_id,
    );

    if (!finalizedBrand) return;

    setSelectedBrand({
      id: finalizedBrand.id,
      label: finalizedBrand.display_name || finalizedBrand.name,
      value: finalizedBrand.display_name || finalizedBrand.name,
    });
    setSelectedOnboardingBrand(null);
  }, [
    adminAuthToken,
    brandList?.list,
    selectedOnboardingBrand,
    setSelectedBrand,
    setSelectedOnboardingBrand,
  ]);

  const parentChildBrandData = useMemo(
    () =>
      user
        ? [
            {
              id: user?.data?.parent_brand?.id,
              value: user?.data?.parent_brand?.id,
              label: user?.data?.parent_brand?.name,
            },
            ...(user?.data?.child_brands || []).map((childBrand) => ({
              id: childBrand.id,
              value: childBrand.id,
              label: childBrand.name,
            })),
          ]
        : [],
    [user],
  );

  const handleBrandChangeByAdmin = (newBrand) => {
    setSelectedBrand(newBrand);
    setSelectedOnboardingBrand(null);
    queryClient.invalidateQueries({
      predicate: (query) => !["brandList"].includes(query.queryKey[0]),
      refetchType: "all",
    });
    clearStorageExcept([
      "Token",
      "admin-brand",
      "user_type",
      "admin-onboarding-brand",
    ]);
    // Hard-navigate to the root instead of reloading the current path — the
    // previously open route may be scoped to the old brand (e.g. a campaign
    // id that doesn't exist for the new one) and would just fail its API calls.
    // DefaultRedirect (mounted at "/") resolves the right landing page.
    window.location.href = "/";
  };

  const handleOnboardingBrandChangeByAdmin = (newBrand) => {
    setSelectedOnboardingBrand(newBrand);
    setSelectedBrand(null);
    queryClient.invalidateQueries({
      predicate: (query) =>
        !["onboardingBrandList"].includes(query.queryKey[0]),
      refetchType: "all",
    });
    clearStorageExcept([
      "Token",
      "admin-brand",
      "user_type",
      "admin-onboarding-brand",
    ]);
    window.location.href = "/";
  };

  const handleBrandChangeByEmailUser = (newBrand) => {
    setSelectedEmailUserBrand(newBrand);
    queryClient.invalidateQueries({ refetchType: "all" });
    navigate("/");
  };

  useEffect(() => {
    if (!emailUserAuthToken || adminAuthToken) return;
    if (user?.data?.user_type !== EMAIL_USER_TYPE) return;

    const accessibleBrands = user?.data?.accessible_brands || [];
    if (!accessibleBrands.length) return;

    const currentBrandId =
      user?.data?.parent_brand?.id || user?.data?.brand?.id;
    const currentBrand = accessibleBrands.find(
      (brand) => brand.id === currentBrandId,
    );

    if (!currentBrand) return;
    if (selectedEmailUserBrand?.id === currentBrand.id) return;

    setSelectedEmailUserBrand({
      id: currentBrand.id,
      label: currentBrand.display_name || currentBrand.name,
      value: currentBrand.display_name || currentBrand.name,
    });
  }, [
    adminAuthToken,
    emailUserAuthToken,
    selectedEmailUserBrand?.id,
    setSelectedEmailUserBrand,
    user,
  ]);

  useEffect(() => {
    const userId = user?.data?.id;
    const brandId = user?.data?.parent_brand?.id;
    const onboardingApplicationId = user?.data?.onboarding_application_id;
    const status = user?.data?.status;
    const userType = user?.data?.user_type;
    const isLocalhost = LOCALHOST_DOMAINS.includes(window.location.hostname);
    if (brandId) {
      sessionStorage.setItem("brandId", brandId);
    }
    if (!userId || userType !== EMAIL_USER_TYPE || isLocalhost) {
      setUserPropertiesGa({
        emailUserId: null,
        onboardingApplicationId: null,
        brandId: null,
      });
      console.log("GA user id: null");
      return;
    }

    if (status === ONBOARDING_USER_STATUS) {
      setUserPropertiesGa({
        emailUserId: userId,
        onboardingApplicationId,
        brandId: null,
      });
    } else if (status === APPROVED_USER_STATUS) {
      setUserIdClarity(brandId);
      setUserPropertiesGa({
        emailUserId: userId,
        brandId,
        onboardingApplicationId: null,
      });
    } else if (status === APPROVED_THROUGH_DIY_FLOW_USER_STATUS) {
      setUserIdClarity(brandId);
      setUserPropertiesGa({
        emailUserId: userId,
        brandId,
        onboardingApplicationId,
      });
    }

    console.log("GA/Clarity props set");
  }, [setUserIdClarity, user]);

  const loginMutation = useMutation({
    mutationFn: AuthAPI.login,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["currentUser"] });
      const previousUser = queryClient.getQueryData(["currentUser"]);
      queryClient.setQueryData(["currentUser"], {
        email: variables.email,
        first_name: "first name",
        last_name: "placeholder",
        parent_brand: {
          id: 0,
          name: "",
          logo_url: variables?.logo_url,
        },
        brandLogo: variables.brandLogo,
        isLoading: true,
        child_brands: [],
        profile_pic_url: variables.profile_pic_url,
        features: [],
        is_admin: false,
        user_type: "emailUser",
        status: "PENDING",
        current_onboarding_step: 3,
        onboarding_application_status: "DRAFT",
        onboarding_application_id: 1,
      });
      return { previousUser };
    },
    onSuccess: async (data, variables) => {
      // const storage = variables.remember_me ? localStorage : sessionStorage;
      const storage = localStorage;
      [localStorage, sessionStorage].forEach((s) => s.removeItem("Token"));
      [localStorage, sessionStorage].forEach((s) => s.removeItem("user_type"));
      [localStorage, sessionStorage].forEach((s) =>
        s.removeItem("email-user-brand"),
      );
      // [localStorage, sessionStorage].forEach(s => s.removeItem('user_status'));
      storage.setItem("authToken", data.data.token);
      storage.setItem("user_type", data.data.user_type);
      // storage.setItem('user_status', data.data.status);
      setEmailUserAuthToken(data.data.token);
      setAdminAuthToken(null);
      queryClient.removeQueries({ queryKey: ["currentUser"] });
      await queryClient.prefetchQuery({
        queryKey: ["currentUser"],
        queryFn: () => AuthAPI.getCurrentUser(),
      });
      const safeRedirectPath = variables.safeRedirectPath || "/";
      navigate(safeRedirectPath, {
        replace: true,
        state: {
          ...(location.state || {}),
          __cleanHistory: true,
        },
      });
    },

    onError: (error, variables) => {
      if (error?.response?.data?.message === "Email not registered.") {
        triggerGa({
          category: "Error",
          action: "email_not_registered",
          label: "",
          label2: "",
          page: "login",
          screenName: "login",
        });
      }
      if (error?.response?.data?.message === "Incorrect password.") {
        triggerGa({
          category: "Error",
          action: "incorrect_password",
          label: "",
          label2: "",
          page: "login",
          screenName: "login",
        });
      }
      [localStorage, sessionStorage].forEach((storage) => {
        storage.removeItem("authToken");
        storage.removeItem("user_type");
        // storage.removeItem('user_status');
      });
      variables.setError?.(
        error?.response?.data?.message || "Invalid credentials or server error",
      );
    },
  });

  const adminLoginMutation = useMutation({
    mutationFn: AuthAPI.adminLogin,
    onSuccess: async (data, variables) => {
      [localStorage, sessionStorage].forEach((s) => s.removeItem("authToken"));
      [localStorage, sessionStorage].forEach((s) => s.removeItem("user_type"));
      [localStorage, sessionStorage].forEach((s) =>
        s.removeItem("email-user-brand"),
      );
      sessionStorage.setItem("Token", data.token);
      sessionStorage.setItem("user_type", "adminUser");
      // sessionStorage.setItem('user_status', data.data.status);
      setAdminAuthToken(data.token);
      setEmailUserAuthToken(null);

      queryClient.removeQueries({ queryKey: ["currentUser"] });
      const adminBrandContext = selectedOnboardingBrand
        ? { onboarding_brand_id: selectedOnboardingBrand.id }
        : { brand_id: selectedBrand?.id };
      await queryClient.prefetchQuery({
        queryKey: ["currentUser", adminBrandContext],
        queryFn: () =>
          AuthAPI.getCurrentUser({
            brand_id: selectedBrand?.id,
            onboarding_brand_id: selectedOnboardingBrand?.id,
          }),
      });
      navigate("/", {
        replace: true,
        state: {
          ...(location.state || {}),
          __cleanHistory: true,
        },
      });
    },
    onError: (error, variables) => {
      [localStorage, sessionStorage].forEach((storage) => {
        storage.removeItem("Token");
        storage.removeItem("user_type");
        // storage.removeItem('user_status');
      });
      variables.setError?.(
        error?.response?.data?.message || "Invalid credentials or server error",
      );
    },
  });

  const logout = async () => {
    try {
      await AuthAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      [localStorage, sessionStorage].forEach((storage) => {
        storage.removeItem("authToken");
        storage.removeItem("Token");
        storage.removeItem("user_type");
        // storage.removeItem('user_status');
        storage.removeItem("admin-brand");
        storage.removeItem("admin-onboarding-brand");
        storage.removeItem("email-user-brand");
        storage.removeItem("brandId");
      });
      queryClient.clear();
      queryClient.removeQueries({ queryKey: ["currentUser"] });
      setEmailUserAuthToken(null);
      setAdminAuthToken(null);
      setUserPropertiesGa({
        emailUserId: null,
        onboardingApplicationId: null,
        brandId: null,
      });
      setUserIdClarity(null);
    }
  };

  const sendOTPMutation = useMutation({
    mutationFn: AuthAPI.sendOTP,
    onSuccess: (data) => {
      console.log("OTP sent successfully", data);
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: AuthAPI.resetPassword,
    onSuccess: () => {
      navigate("/login", {
        state: { successMessage: "Password reset successful!" },
      });
    },
    onError: (error) => {
      console.error("Password reset failed:", error);
    },
  });

  function hasFeature(feature, subFeature) {
    if (!user?.data?.features) return false;
    if (!user?.data?.features[feature]) return false;
    if (!subFeature) return true;
    return user.data.features[feature].includes(subFeature);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        brandFields: parentChildBrandData,
        emailUserAuthToken,
        setEmailUserAuthToken,
        adminAuthToken,
        setAdminAuthToken,
        hasFeature,
        isUserDataLoading,
        isUserDataError,
        userError,
        loginMutation,
        adminLoginMutation,
        logout,
        sendOTPMutation,
        resetPasswordMutation,
        brandList,
        onboardingBrandList,
        selectedBrand,
        setSelectedBrand,
        handleBrandChangeByAdmin,
        selectedOnboardingBrand,
        handleOnboardingBrandChangeByAdmin,
        selectedEmailUserBrand,
        handleBrandChangeByEmailUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
